import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from '@angular/core';

@Component({
  selector: 'mb-resizable-container',
  templateUrl: './resizable-container.component.html',
  styleUrls: ['./resizable-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResizableContainerComponent implements AfterViewInit {
  private _direction = 'horizontal'; // vertical | horizontal
  public area = 'right'; // top | left | bottom | right

  @Output() resizeStart = new EventEmitter();
  @Output() initUpdate = new EventEmitter();
  @Output() resizeUpdate = new EventEmitter();
  @Output() resizeEnd = new EventEmitter();
  @Output() isOutsideClick = new EventEmitter<boolean>();

  @Input() minWidth = 0;
  @Input() maxWidth = 0;

  @Input() minHeight = 0;
  @Input() maxHeight = 0;

  @Input() canResize = true;
  @Input()
  set dragArea(v: string) {
    this.area = v;
    if (v === 'top' || v === 'bottom') {
      this._direction = 'vertical';
    } else if (v === 'left' || v === 'right') {
      this._direction = 'horizontal';
    }
  }
  get dragArea() {
    return this.area;
  }

  @HostListener('document:click', ['$event'])
  public outsideClick(event: any) {
    if (!this.elRef.nativeElement.contains(event.target)) {
      this.isOutsideClick.emit(true);
    }
  }

  constructor(public elRef: ElementRef) {}

  dragStart(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    this.resizeStart.emit();
    const host = this.elRef.nativeElement;
    const startValue = this._direction === 'horizontal' ? host.offsetWidth : host.offsetHeight;
    const startPos = this._direction === 'horizontal' ? e.screenX : e.screenY;
    document.body.style.userSelect = 'none';

    const mousemove = (e: MouseEvent) => {
      const propToChange = this._direction === 'horizontal' ? 'width' : 'height';
      let newValue = startValue - this.calcDiff(e.screenX, e.screenY, startPos);
      if (propToChange === 'width') {
        if (this.minWidth && newValue < this.minWidth) {
          newValue = this.minWidth;
        }
        if (this.maxWidth && newValue > this.maxWidth) {
          newValue = this.maxWidth;
        }
      }
      if (propToChange === 'height') {
        if (this.minHeight && newValue < this.minHeight) {
          newValue = this.minHeight;
        }
        if (this.maxHeight && newValue > this.maxHeight) {
          newValue = this.maxHeight;
        }
      }
      this.resizeUpdate.emit({
        height: this.elRef.nativeElement.clientHeight,
        width: this.elRef.nativeElement.clientWidth,
      });
      host.style[propToChange] = newValue + 'px';
    };

    const dragEnd = () => {
      document.removeEventListener('mousemove', mousemove);
      document.removeEventListener('mouseup', dragEnd);
      this.resizeEnd.emit();
      document.body.style.userSelect = '';
    };

    document.addEventListener('mouseup', dragEnd);
    document.addEventListener('mousemove', mousemove);
  }

  touchStart(e: TouchEvent) {
    e.preventDefault();
    e.stopPropagation();
    this.resizeStart.emit();
    const touch = e.touches[0] || e.changedTouches[0];
    const host = this.elRef.nativeElement;
    const startValue = this._direction === 'horizontal' ? host.offsetWidth : host.offsetHeight;
    const startPos = this._direction === 'horizontal' ? touch.clientX : touch.clientY;
    document.body.style.userSelect = 'none';
    document.body.style.overflow = 'hidden';

    const touchmove = (e: TouchEvent) => {
      const _touch = e.touches[0] || e.changedTouches[0];
      const propToChange = this._direction === 'horizontal' ? 'width' : 'height';
      let newValue = startValue - this.calcDiff(_touch.clientX, _touch.clientY, startPos);
      if (propToChange === 'width') {
        if (this.minWidth && newValue < this.minWidth) {
          newValue = this.minWidth;
        }
        if (this.maxWidth && newValue > this.maxWidth) {
          newValue = this.maxWidth;
        }
      }
      if (propToChange === 'height') {
        if (this.minHeight && newValue < this.minHeight) {
          newValue = this.minHeight;
        }
        if (this.maxHeight && newValue > this.maxHeight) {
          newValue = this.maxHeight;
        }
      }
      host.style[propToChange] = newValue + 'px';
      this.resizeUpdate.emit({
        height: this.elRef.nativeElement.clientHeight,
        width: this.elRef.nativeElement.clientWidth,
      });
    };

    const dragEnd = () => {
      document.removeEventListener('touchmove', touchmove);
      document.removeEventListener('touchend', dragEnd);
      this.resizeEnd.emit();
      document.body.style.userSelect = '';
    };

    document.addEventListener('touchend', dragEnd);
    document.addEventListener('touchmove', touchmove);
  }

  calcDiff(x: number, y: number, startPos: number) {
    let pos, diff;
    if (this._direction === 'horizontal') {
      pos = x;
      diff = this.dragArea === 'left' ? pos - startPos : startPos - pos;
    } else {
      pos = y;
      diff = this.dragArea === 'top' ? pos - startPos : startPos - pos;
    }
    return diff;
  }

  ngAfterViewInit() {
    this.initUpdate.emit({
      height: this.elRef.nativeElement.clientHeight,
      width: this.elRef.nativeElement.clientWidth,
    });
  }
}
